// Shared tool-life helpers for the HMI runtime.
// Kept declarative + machine-type agnostic: works for ANY set of TLM_<key>_COUNT
// keys the field metadata / live state happens to expose (T1..Tn, DIE, PUNCH, …).

/**
 * Extract live TLM tool rows from a machine state object.
 * Finds every `TLM_<KEY>_COUNT`, pairs it with its `TLM_<KEY>_PRESET`, and
 * returns rows sorted numerically (T1,T2,…) when numeric, else alphabetically.
 * @param {Record<string, unknown>} state
 * @returns {{ key: string, count: number, preset: number, pct: number }[]}
 */
export function extractTlmTools(state) {
  const keys = [];
  for (const k of Object.keys(state || {})) {
    if (k.startsWith('TLM_') && k.endsWith('_COUNT')) {
      keys.push(k.slice(4, -6)); // 'TLM_DIE_COUNT' → 'DIE', 'TLM_T1_COUNT' → 'T1'
    }
  }
  keys.sort((a, b) => {
    const na = parseInt(a.replace(/^T/i, ''), 10);
    const nb = parseInt(b.replace(/^T/i, ''), 10);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return a.localeCompare(b);
  });
  return keys.map((key) => {
    const count = Number(state[`TLM_${key}_COUNT`] ?? 0);
    const preset = Number(state[`TLM_${key}_PRESET`] ?? 0);
    const pct = preset > 0 ? Math.round((count / preset) * 100) : 0;
    return { key, count, preset, pct };
  });
}

/** Wear severity for a tool row → drives the CRT color (green/amber/red). */
export function toolWearLevel(pct) {
  if (pct >= 100) return 'over';
  if (pct >= 80) return 'warn';
  return 'ok';
}

// Canonical CNC axis order so the DRO reads X, Y, Z, A, B, C (rotary last).
const AXIS_ORDER = ['X', 'Y', 'Z', 'A', 'B', 'C'];

/**
 * Extract DRO axis rows from a machine state object.
 * Finds every `POS_<AXIS>` the engine writes (X/Z today, forward-compat for Y and
 * any rotary axis) and returns them in canonical CNC order. State-derived, exactly
 * like {@link extractTlmTools} — no field-def / schema rows required.
 * @param {Record<string, unknown>} state
 * @returns {{ axis: string, value: number }[]}
 */
export function extractDroAxes(state) {
  const rows = [];
  for (const k of Object.keys(state || {})) {
    const m = /^POS_([A-Z][A-Z0-9]*)$/.exec(k);
    if (m) rows.push({ axis: m[1], value: Number(state[k]) });
  }
  rows.sort((a, b) => {
    const ia = AXIS_ORDER.indexOf(a.axis);
    const ib = AXIS_ORDER.indexOf(b.axis);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.axis.localeCompare(b.axis);
  });
  return rows;
}

/**
 * Extract spindle telemetry (RPM + % load) from a machine state object.
 * Returns null when the engine has written neither key (e.g. a Studio mock), so
 * the panel can skip the block entirely.
 * @param {Record<string, unknown>} state
 * @returns {{ rpm: number|null, load: number|null } | null}
 */
export function extractSpindle(state) {
  const s = state || {};
  const has = (k) => Object.prototype.hasOwnProperty.call(s, k);
  if (!has('SPINDLE_RPM') && !has('SPINDLE_LOAD')) return null;
  return {
    rpm: has('SPINDLE_RPM') ? Number(s.SPINDLE_RPM) : null,
    load: has('SPINDLE_LOAD') ? Number(s.SPINDLE_LOAD) : null,
  };
}

/** Format a signed DRO value in mm (fixed 3 dp), e.g. -120.000 / +025.123. */
export function formatDroMm(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  return `${n < 0 ? '-' : '+'}${Math.abs(n).toFixed(3)}`;
}


/**
 * Tool OFFSETS out of the state bag: `OFS_<toolKey>_<X|Z|R>` → one row per tool.
 *
 * Mirrors the real control's OFFSET / WEAR page, whose columns are literally `X | Z | R` over rows
 * `W001…` (photographed on the Rassini lathe; see focas-playground/docs/31). A family the machine does
 * not carry is ABSENT rather than zero — a mill has no per-tool Z wear — so the caller must render a
 * blank cell for it and not a 0.000, which would claim a value the control does not have.
 *
 * Row numbering follows the same rule as tool-life slots: numeric tools keep their number, named tools
 * (DIE, NOZZLE) take slots above the highest numeric one, so a tool sits on the same row in both.
 */
export function extractOffsets(state) {
  const byTool = new Map();
  for (const k of Object.keys(state || {})) {
    const m = /^OFS_(.+)_([XZR])$/.exec(k);
    if (!m) continue;
    const [, key, family] = m;
    if (!byTool.has(key)) byTool.set(key, { key, X: null, Z: null, R: null });
    byTool.get(key)[family] = Number(state[k]);
  }
  const toolNumber = (k) => {
    const m = /^[Tt]?(\d+)$/.exec(k);
    return m ? parseInt(m[1], 10) : null;
  };
  const rows = [...byTool.values()];
  rows.sort((a, b) => {
    const na = toolNumber(a.key);
    const nb = toolNumber(b.key);
    if (na != null && nb != null) return na - nb;
    if (na != null) return -1;
    if (nb != null) return 1;
    return a.key.localeCompare(b.key);
  });
  const maxNumeric = rows.reduce((mx, r) => Math.max(mx, toolNumber(r.key) ?? 0), 0);
  let next = maxNumeric + 1;
  return rows.map((r) => ({ ...r, slot: toolNumber(r.key) ?? next++ }));
}

/** Format one offset cell. `null` means the machine does not carry that family — render it blank. */
export function formatOffsetMm(v) {
  return v == null ? '' : v.toFixed(3);
}
